<template>
  <div
    ref="el"
    class="xfield form-field"
    :class="{ checkbox: schema.type === 'checkbox' }"
  >
    <label
      v-if="
        field.type !== 'checkbox' && field.type !== 'radio' && !field.hideLabel
      "
      :class="field.hideLabel ? 'hide' : ''"
    >
      <slot name="label">
        {{ field.label ? field.label : "&nbsp;" }}<i v-if="field.required">*</i>
      </slot>
      <div v-if="hasHelpSlot" class="help">
        <font-awesome-icon class="info" icon="fa-circle-info" />
        <div class="tooltip" :class="field.tooltipLocation">
          <slot name="help"> Shalom from the tooltip! </slot>
        </div>
      </div>
    </label>
    <div
      v-if="
        $slots.hint &&
        schema.hintPosition === 'top' &&
        field.type !== 'checkbox' &&
        field.type !== 'radio'
      "
      class="hint"
    >
      <slot name="hint"></slot>
    </div>
    <select
      v-if="field.type === 'select'"
      v-bind="$attrs"
      :value="modelValue"
      class="form-control select"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      @change="
        $emit(
          'update:modelValue',
          ($event.target as HTMLSelectElement).value !== ''
            ? ($event.target as HTMLSelectElement).value
            : undefined
        );
        validate(schema, ($event.target as HTMLSelectElement).value, compare);
      "
      @blur="
        handleOnBlur();
        validate(schema, modelValue, compare);
      "
    >
      <option value="" :disabled="schema.required">
        <slot name="default-option"></slot>
      </option>
      <option
        v-for="(option, index) in options"
        :value="option.value ? option.value : option"
        :key="index"
        :selected="option === modelValue"
      >
        {{ option.label ? option.label : option }}
      </option>
    </select>
    <label
      v-else-if="field.type === 'checkbox'"
      v-bind="$attrs"
      class="checkbox-container"
    >
      <slot name="label">{{ field.label ? field.label : "&nbsp;" }}</slot>
      <input
        v-bind="$attrs"
        class="form-check-input"
        type="checkbox"
        :class="{
          'is-invalid': schema.touched && !schema.valid,
        }"
        :checked="modelValue as any"
        @change="
          $emit('update:modelValue', ($event.target as any).checked);
          validate(schema, ($event.target as any).checked, compare);
        "
      />
      <span class="checkmark"></span>
    </label>
    <div v-else-if="field.type === 'radio' && options && options.length > 0">
      <label v-if="!field.hideLabel">
        <slot name="label">
          {{ field.label ? field.label : "&nbsp;" }}
        </slot>
        <div v-if="hasHelpSlot" class="help">
          <font-awesome-icon icon="fas fa-circle-info" />
          <div class="tooltip" :class="field.tooltipLocation">
            <slot name="help"> Shalom from the tooltip! </slot>
          </div>
        </div>
      </label>
      <div v-if="$slots.hint && schema.hintPosition === 'top'" class="hint">
        <slot name="hint"></slot>
      </div>
      <div v-bind="$attrs" class="radio-btns-container">
        <label
          v-for="option in options"
          :key="option.value"
          class="xradio radio-container"
        >
          <slot name="radioLabel">{{ option.label ? option.label : "" }}</slot>
          <input
            type="radio"
            ref="radioInput"
            :checked="modelValue === option.value"
            :value="option.value"
            :name="field.name || 'radio-group'"
            @change="
              $emit(
                'update:modelValue',
                option.value !== undefined ? option.value : option
              );
              validate(schema, ($event.target as any).value, compare);
            "
            v-bind="$attrs"
          />
          <span class="checkmark"></span>
        </label>
      </div>
    </div>
    <!-- <div v-else-if="field.type === 'dob'">
    </div> -->
    <div
      v-else-if="field.type === 'autocomplete'"
      class="autocomplete-container"
    >
      <input
        ref="autocompleteInputRef"
        v-bind="$attrs"
        class="form-control cuscd-desc"
        autocomplete="off"
        :type="schema.type === 'compare' ? schema.compareType : schema.type"
        :name="schema.name ? schema.name : undefined"
        :class="{
          'is-invalid': schema.touched && !schema.valid,
        }"
        :value="autocompleteSearch"
        :placeholder="field.placeholder ? field.placeholder : undefined"
        @blur="handleAutoCompleteBlur(schema, modelValue, compare)"
        @paste="handlePaste"
        @input="onAutocompleteChange($event)"
        @focus="handleAutoCompleteFocus()"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keyup="
          validateOnKeyUp || schema.touched
            ? validate(schema, modelValue, compare)
            : undefined
        "
        :title="autocompleteSearch"
      />
      <div class="results-wrapper">
        <div
          ref="autocompleteSearchInputError"
          v-show="
            autocompleteSearch &&
            autoCompleteIsOpen &&
            autoCompleteResults.length === 0
          "
          class="results-error"
        >
          <slot name="autocomplete-error">
            {{ autoCompleteResultsErrorMessage }}
          </slot>
        </div>
        <!-- <ul v-show>
          <li
            v-for="(result, i) in autocompleteOptions"
            :key="i"
            class="autocomplete-result"
            :class="{ 'is-active': i === autoCompleteArrowCounter }"
            @click="setAutocompleteValue(result)"
          >
            {{
              schema.autocompleteProperty
                ? result[schema.autocompleteProperty]
                : result
            }}
          </li>
        </ul> -->
        <ul
          v-show="autoCompleteIsOpen && autoCompleteResults.length > 0"
          class="autocomplete-results"
          ref="autocompleteResults"
        >
          <li
            v-for="(result, i) in autoCompleteResults"
            :key="i"
            class="autocomplete-result"
            :class="{ 'is-active': i === autoCompleteArrowCounter }"
            @click="setAutocompleteValue(schema, result)"
          >
            {{
              schema.autocompleteProperty
                ? result[schema.autocompleteProperty]
                : result
            }}
          </li>
        </ul>
      </div>
    </div>
    <input
      v-else-if="field.type === 'money'"
      v-bind="$attrs"
      type="text"
      class="form-control money"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      v-maska
      :data-maska="field?.allowDecimals === false ? '0' : '0.99'"
      data-maska-tokens="0:\d:multiple|9:\d:optional"
      :name="schema.name ? schema.name : undefined"
      :value="modelValue"
      :maxLength="field.maxLength ? field.maxLength : undefined"
      :allowZero="field.allowZero ? field.allowZero : undefined"
      :allowDecimals="field.allowDecimals ? field?.allowDecimals : undefined"
      @keydown="validateInput($event)"
      @paste="handlePaste($event)"
      @input="$emit('update:modelValue', ($event.target as any).value)"
      @blur="validate(schema, modelValue, compare)"
      @keyup="
        validateOnKeyUp || schema.touched
          ? validate(schema, modelValue, compare)
          : undefined
      "
    />
    <input
      v-else-if="field.type === 'nationalIdNumber'"
      v-bind="$attrs"
      ref="ssnInput"
      type="text"
      class="form-control"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      :name="schema.name ? schema.name : undefined"
      :value="cleanedValue || modelValue"
      :placeholder="field.placeholder ? field.placeholder : undefined"
      @focus="cleanedValue = undefined"
      @paste="handlePaste($event)"
      @keydown="validateOnKeyDown ? validateSsnInput($event) : undefined"
      @input="$emit('update:modelValue', ($event.target as any).value)"
      @blur="
        handleOnBlur();
        validateNationalIdNumber(schema, modelValue, compare);
      "
      @keyup="
        validateOnKeyUp || schema.touched
          ? validateNationalIdNumber(schema, modelValue, compare)
          : undefined
      "
    />
    <input
      v-else-if="field.type === 'zipcode'"
      v-bind="$attrs"
      ref="zipInput"
      type="text"
      class="form-control"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      :title="field.title ? field.title : undefined"
      :name="schema.name ? schema.name : undefined"
      :value="cleanedValue || modelValue"
      :placeholder="field.placeholder ? field.placeholder : undefined"
      :maxlength="(modelValue as string).includes('-') ? 10 : 9"
      @paste="handlePaste($event)"
      @focus="cleanedValue = undefined"
      @keydown="validateZipCodeInput($event)"
      @keypress="validateZipCodeInput($event)"
      @input="$emit('update:modelValue', ($event.target as any).value)"
      @blur="
        handleOnBlur();
        validate(schema, modelValue, compare);
      "
      @keyup="
        validateOnKeyUp || schema.touched
          ? validate(schema, modelValue, compare)
          : undefined
      "
    />
    <!-- "schema.type === 'phone' ? '(###) ###-####' : undefined" -->
    <input
      v-else-if="field.type === 'phone'"
      v-bind="$attrs"
      class="form-control cuscd-desc"
      v-maska
      data-maska="###-###-####"
      :type="schema.type === 'compare' ? schema.compareType : schema.type"
      :name="schema.name ? schema.name : undefined"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      :value="modelValue"
      :placeholder="field.placeholder ? field.placeholder : undefined"
      :autocomplete="field.autocomplete ? field.autocomplete : undefined"
      :maxlength="field.maxLength"
      :title="field.title ? field.title : undefined"
      :pattern="field.pattern ? field.pattern : undefined"
      @paste="handlePaste($event)"
      @keydown="validateInput($event)"
      @keypress="validateInput($event)"
      @input="$emit('update:modelValue', ($event.target as any).value)"
      @blur="undefined"
      @keyup="
        validateOnKeyUp || schema.touched
          ? validate(schema, modelValue, compare)
          : undefined
      "
    />
    <textarea
      v-else-if="field.type === 'textarea'"
      v-bind="$attrs"
      class="form-control"
      :name="schema.name ? schema.name : undefined"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
      }"
      :value="(modelValue as string)"
      :maxlength="field.maxLength ? field.maxLength : undefined"
      @input="$emit('update:modelValue', ($event.target as any).value)"
      @keyup="
        (validateOnKeyUp || schema.touched) && modelValue
          ? validate(schema, modelValue, compare)
          : undefined
      "
    ></textarea>
    <input
      v-else
      v-bind="$attrs"
      class="form-control cuscd-desc"
      :type="schema.type === 'compare' ? schema.compareType : schema.type"
      :name="schema.name ? schema.name : undefined"
      :class="{
        'is-invalid': schema.touched && !schema.valid,
        valid:
          schema.touched &&
          schema.valid &&
          (schema.type === 'email' ||
            schema.type === 'phone' ||
            schema.type === 'password' ||
            schema.type === 'compare'),
      }"
      :value="modelValue"
      :placeholder="field.placeholder ? field.placeholder : undefined"
      :autocomplete="field.autocomplete ? field.autocomplete : undefined"
      :maxlength="field.maxLength"
      :title="field.title ? field.title : undefined"
      :pattern="field.pattern ? field.pattern : undefined"
      @paste="handlePaste($event)"
      @keydown="validateInput($event)"
      @keypress="validateInput($event)"
      @input="$emit('update:modelValue', ($event.target as any).value)"
      @blur="handleBlur(schema, modelValue, compare)"
      @keyup="
        (validateOnKeyUp || schema.touched) && modelValue
          ? validate(schema, modelValue, compare)
          : undefined
      "
    />
    <div v-if="$slots.hint && schema.hintPosition === 'bottom'" class="hint">
      <slot name="hint"></slot>
    </div>

    <ul
      v-if="
        schema.errors && schema.errors.length >= 1 && !field.hideErrorMessage
      "
      class="invalid-feedback"
      :class="{
        multiple: schema.errors && schema.errors.length > 1,
      }"
    >
      <li v-for="(error, index) in schema.errors" :key="index">
        {{ error }}
      </li>
    </ul>

    <div v-if="$slots.errors" class="invalid-feedback">
      <slot name="errors"></slot>
    </div>
    <div v-if="$slots.yupErrors">
      <slot name="yupErrors"></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  onBeforeMount,
  reactive,
  ref,
  onUnmounted,
  useSlots,
  computed,
  watch,
  onMounted,
} from "vue";
import useValidation from "@/use/validation";
import type { FormField, FieldSchema, Option } from "@/models/form";
import useFormatter from "@/use/formatter";
import type { PropType } from "vue";
import { vMaska, MaskInput } from "maska";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import useHtmlUtilities from "@/use/htmlUtilities";
import type { School } from "@/types/school";
import * as yup from "yup";

const props = defineProps({
  schema: {
    type: Object as PropType<FieldSchema>,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, Object, null],
    default: "",
  },
  options: {
    type: Array as PropType<Array<Option>>,
    default: [] as Array<Option>,
  },
  autocompleteOptions: {
    type: Array as PropType<Array<any>>,
    default: [] as Array<any>,
  },
  compare: {
    type: [String, Number, Boolean, Object],
    required: false,
  },
  validateOnKeyDown: {
    type: Boolean,
    default: true,
  },
  validateOnKeyUp: {
    type: Boolean,
    default: true,
  },
  errors: {
    type: Array as PropType<Array<string>> | undefined | null,
    required: false,
  },
  rules: {
    type: Object as PropType<yup.Schema> | undefined,
    required: false,
    default: undefined,
  },
  useYupRules: {
    type: Boolean,
    default: false,
    required: false,
  },
});
const emit = defineEmits<{
  (
    e: "update:modelValue",
    value: number | boolean | string | object | undefined | null
  ): void;
  (e: "update:valid", value: boolean | undefined): void;
  (e: "update:touched", value: boolean | undefined): void;
  (e: "update:errors", value: string[] | undefined | null): void;
}>();

const slots = useSlots();
const ssnInput = ref(null);
const selectInput = ref(null);
const phoneValue = ref("");
const rawValue = ref("");

const hasHelpSlot = computed<boolean>(() => !!slots.help);
const autoCompleteResultsErrorMessage = computed<string>(() => {
  if (
    props?.schema?.autocompleteProperty === "schoolName" &&
    autocompleteSearch.value?.trim()?.length > 0
  ) {
    return "Your school is not on our eligible schools list, and your school must be listed to be eligible for disbursements. Please check your spelling.";
  }
  if (field?.autoCompleteResultsErrorMessage) {
    return field?.autoCompleteResultsErrorMessage;
  }
  return "Error";
});

const { validate, validateNationalIdNumber } = useValidation();
const { formatPhoneNumber, formatMoney } = useFormatter();
const { isElementVisible } = useHtmlUtilities();
const autocompleteResults = ref(null);
const autocompleteSearchInputError = ref("");
const el = ref();

const autocompleteInputRef = ref<HTMLInputElement | null>(null);
// initialize maska plugin
new MaskInput("[data-maska]");

let autocompleteSearch = ref<string>("");
let field = reactive({} as FormField);
let cleanedValue = ref<string | undefined>("");
let autoCompleteResults = reactive([]) as Option[];
let autoCompleteIsOpen = ref<boolean>(false);
let autoCompleteArrowCounter = ref<number>(-1);

const filteredSchools = computed(() => {
  const searchText = autocompleteSearch.value.toLowerCase();
  const options = props?.autocompleteOptions || [];

  return options.filter((school: School) => {
    if (props?.schema?.autocompleteProperty && searchText.length > 0) {
      const schoolName = school[props?.schema?.autocompleteProperty];
      const schoolSearchableName = String(
        school["schoolSearchableName"]
      ).toLowerCase();

      return (
        schoolSearchableName.includes(searchText) ||
        schoolName.includes(searchText)
      );
    }
    return false;
  });
});

const searchResults = computed(() => {
  if (props.schema.type === "autocomplete") {
    const searchText = autocompleteSearch.value.toLowerCase();

    const options = props?.autocompleteOptions || [];

    if (props.schema.name === "school") {
      return filteredSchools.value;
    }

    return options.filter((item) => {
      if (typeof item === "string") {
        return item.toLowerCase().includes(searchText);
      } else if (
        typeof props?.modelValue === "object" &&
        props?.schema?.autocompleteProperty
      ) {
        const autoCompleteProperty = String(
          item[props.schema.autocompleteProperty]
        ).toLowerCase();
        return autoCompleteProperty.includes(searchText);
      }
      return false;
    });
  }
  return [];
});

const ignoreKeys = [
  "Backspace",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
  "Enter",
  "Delete",
  "Shift",
  "CapsLock",
];

function validateInput(event: any) {
  switch (props?.schema.type) {
    case "phone":
      validatePhoneInput(event);
      break;
    case "nationalIdNumber":
      validateSsnInput(event);
      break;
    case "zipcode":
      validateZipCodeInput(event);
      break;
    case "year":
      validateYearInput(event);
      break;
    case "money":
      validateMoneyInput(event);
      break;
    default:
      break;
  }
}

function validateYearInput(event: any) {
  if (ignoreKeys.indexOf(event.key) >= 0) {
    return;
  }
  const validDigit = /^[0-9]$/.test(event.key);
  if (!validDigit && event.key !== "Backspace") {
    event.preventDefault();
  }
}

function validateMoneyInput(event: any) {
  if (ignoreKeys.indexOf(event.key) >= 0) {
    return;
  }
  const validDigit = /^[0-9.]$/.test(event.key) || /^[.]$/.test(event.key);
  if (!validDigit && event.key !== "Backspace") {
    event.preventDefault();
  }
}

function validateSsnInput(event: any) {
  if (ignoreKeys.indexOf(event.key) >= 0) {
    return;
  }
  const validDigit = /^[0-9]$/.test(event.key) || /^[-]$/.test(event.key);
  const cleanedSsn = `${props?.modelValue}${event.key}`.split("-").join("");
  if ((!validDigit && event.key !== "Backspace") || cleanedSsn.length > 9) {
    event.preventDefault();
  }
}
function validateZipCodeInput(event: any) {
  if (ignoreKeys.indexOf(event.key) >= 0 || event.type === "keydown") {
    return;
  }
  const validDigit = /^[0-9]$/.test(event.key) || /^[-]$/.test(event.key);
  if (event.key !== undefined && (!validDigit || event.key === "Backspace")) {
    event.preventDefault();
  }
}
function handleOnBlur() {
  let stringValue = props?.modelValue?.toString() || "";
  switch (field.type) {
    case "nationalIdNumber":
      // We only use the cleanedValue when the user has left the field
      cleanedValue.value = "";
      for (let i = 0; i < stringValue?.length; i++) {
        cleanedValue.value +=
          i <= 2 || i === 4 || i === 5 ? "*" : stringValue[i];
      }
      break;
    case "money":
      cleanedValue = formatMoney(parseInt(stringValue) || 0);
      break;
    default:
      break;
  }
}
function validatePhoneInput(event: any) {
  if (ignoreKeys.indexOf(event.key) >= 0 || event.type === "keydown") {
    return;
  }
  const cleanedPhone = `${event.target.value}${event.key}`.replace(
    /[^0-9]/g,
    ""
  );
  const re = /^[0-9\b]+$/;
  const validChar = /^[0-9]$/.test(event.key) || /^[)( ._-]+$/g.test(event.key);
  if (
    event.key !== undefined &&
    (!validChar || event.key === "Backspace" || cleanedPhone.length > 10)
  ) {
    event.preventDefault();
  }
}
function formatValue() {
  let cleanValue;
  switch (props?.schema.type) {
    case "phone":
      cleanValue = `${props?.modelValue}`.replace(/[^0-9]/g, "");
      if (cleanValue.trim().length === 10) {
        emit("update:modelValue", formatPhoneNumber(cleanValue));
      }
      break;
    case "money":
      cleanValue = `${props?.modelValue}`.replace(/[^$]/g, "");
      emit("update:modelValue", formatMoney(parseInt(cleanValue)));
      break;
    default:
      break;
  }
}
function setFieldTouched(touched?: boolean) {
  emit("update:touched", touched);
}
function handleBlur(schema: any, value: any, compare: any) {
  setFieldTouched(true);
  validate(schema, value, compare);
  if (schema.onBlur) {
    schema.onBlur(schema, value, compare);
  }
  formatValue();
}
function handleClickOutside(event: any) {
  if (!(el.value as HTMLElement).contains(event.target)) {
    autoCompleteIsOpen.value = false;
  }
}
function handleAutoCompleteFocus() {
  if ((autocompleteSearch.value || "").trim()?.length > 0) {
    autoCompleteResults = searchResults.value;
  } else {
    autoCompleteResults = props.autocompleteOptions;
  }

  (autocompleteSearch.value || "").trim()?.length >= 0
    ? (autoCompleteIsOpen.value = true)
    : undefined;
}
function handleAutoCompleteBlur(
  schema: FormField | FieldSchema,
  value: any,
  compare: any
) {
  // if no value is currently selected, reset the search text
  if (
    props.modelValue === "" ||
    (props.modelValue as any)?.id === undefined ||
    (props.modelValue as any)?.id === 0 ||
    (props.modelValue as any)?.id === null
  ) {
    autocompleteSearch.value = "";
    emit("update:modelValue", { id: null });
  }

  // if already selected a value and it matches the search text
  if (
    props.modelValue !== null &&
    props?.schema?.autocompleteProperty &&
    (props.modelValue as any)?.id > 0 &&
    (autocompleteSearch.value === value[props?.schema?.autocompleteProperty] ||
      (props.schema.name === "school" &&
        autocompleteSearch.value === value["schoolSearchableName"]))
  ) {
    autocompleteSearch.value =
      props.modelValue[props?.schema?.autocompleteProperty || ""];

    autoCompleteResults = searchResults.value.filter(
      (s: School) => s.id === (props.modelValue as any)?.id
    );
  }

  // if no search text, reset the field
  if (
    autocompleteSearch.value === "" ||
    (autocompleteSearch.value !== "" &&
      autocompleteSearch.value !==
        (value[props?.schema?.autocompleteProperty || ""] || "").trim())
  ) {
    emit("update:modelValue", { id: null });
  }
  validate(schema, value, compare);
}
function setAutocompleteValue(schema: any, result: any) {
  emit("update:modelValue", result);
  autocompleteSearch.value = props?.schema?.autocompleteProperty
    ? result[props?.schema?.autocompleteProperty]
    : result;
  autoCompleteIsOpen.value = false;
  autoCompleteArrowCounter.value = -1;
  schema.errors = [];
}

function onAutocompleteChange(event) {
  autoCompleteIsOpen.value = true;
  autocompleteSearch.value = event.target.value;

  autoCompleteResults = searchResults.value.slice(0, 25);

  if (autocompleteSearch.value?.length === 0) {
    emit("update:modelValue", { id: null });
    validate(props.schema, { id: null });
  }
}

function handlePaste(event: any) {
  let data = event.clipboardData.getData("text/plain");
  let matched = /^https?:\/\/.*$/.test(data);
  if (matched) {
    event.preventDefault();
  }
}

function onArrowDown() {
  // index appears to be offset by - 1
  if (autoCompleteArrowCounter.value < autoCompleteResults.length - 1) {
    autoCompleteArrowCounter.value = autoCompleteArrowCounter.value + 1;
    // Needs a split second to update the view
    setTimeout(() => {
      const results = autocompleteResults.value as any;
      const active = results.querySelector(".is-active");
      const isVisible = isElementVisible(active, results);
      if (!isVisible) {
        results.scrollTo(active.offsetBottom, active.offsetTop);
      }
    }, 50);
  }
}

function onArrowUp() {
  if (autoCompleteArrowCounter.value > 0) {
    autoCompleteArrowCounter.value = autoCompleteArrowCounter.value - 1;
    // Needs a split second to update the view
    setTimeout(() => {
      const results = autocompleteResults.value as any;
      const active = results.querySelector(".is-active");
      const isVisible = isElementVisible(active, results);
      if (!isVisible) {
        results.scrollTo(active.offsetTop, active.offsetBottom);
      }
    }, 50);
  }
}

function onEnter() {
  if (autoCompleteArrowCounter.value > -1) {
    const result: any = autoCompleteResults[autoCompleteArrowCounter.value];
    emit("update:modelValue", result);
    autocompleteSearch.value = props?.schema?.autocompleteProperty
      ? result[props?.schema?.autocompleteProperty]
      : result;
    autoCompleteArrowCounter.value = -1;
    autoCompleteIsOpen.value = false;
  }
}

function onEscape() {
  emit("update:modelValue", { id: null });
}

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (newValue == oldValue) {
      return;
    }

    // update autocomplete on external change
    if (
      newValue &&
      props?.schema?.autocompleteProperty &&
      props?.schema?.type === "autocomplete"
    ) {
      autocompleteSearch.value =
        newValue[props.schema.autocompleteProperty || ""];
    }

    if (props?.schema?.label !== field.label) {
      field.label = props.schema.label;
    }
  }
);

// using external errors, yup validation
watch(
  () => props.errors,
  () => {
    if (props.useYupRules) {
      emit("update:errors", props.errors);
      emit("update:valid", props.errors?.length === 0);
    }
  }
);

onBeforeMount(() => {
  // create form field
  if (props?.schema) {
    field = {
      ...props?.schema,
      touched: false,
      valid: false,
    } as FormField;
  }

  if (props?.schema?.type === "autocomplete") {
    autocompleteSearch.value = (props?.modelValue as any)[
      props?.schema?.autocompleteProperty || ""
    ];
  }
  // If we don't do this it defaults booleans as true
  if (field.type === "checkbox" && props?.modelValue === "") {
    emit("update:modelValue", false);
  }
});
onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  if (
    props?.schema?.type === "autocomplete" &&
    props.autocompleteOptions?.length > 0
  ) {
    autoCompleteResults = props.autocompleteOptions;
  }

  setFieldTouched(undefined);
});
onUnmounted(() => {
  // Destroy plugin
  const myMask = new MaskInput("[data-maska]");
  myMask.destroy();
  document.removeEventListener("click", handleClickOutside);
});

defineExpose({
  autocompleteInputRef,
  autocompleteSearch,
  setFieldTouched,
});
</script>

<style scoped src="@/assets/css/baseformfield.scss" lang="scss"></style>
